import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../lib/AuthContext";
import { useSettings } from "../lib/SettingsContext";
import { db } from "../lib/firebase";
import { addDoc, collection, getDocs, query, serverTimestamp, where, doc, updateDoc } from "firebase/firestore";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Spinner, PageLoader } from "../components/Spinner";
import { useCatalog } from "../lib/CatalogContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

// Keyed by package doc ID (stable) rather than the editable `code`/`name`
// fields, so renaming a plan's display text in Firestore doesn't break
// its border color.
const PLAN_BORDER_COLORS = {
  saver: "border-[#00D05A]",       // green
  saver_pro: "border-[#002FA7]",   // blue
  premium: "border-[#A1A1AA]",     // grey
  premium_pro: "border-[#FFC000]", // gold
};

export default function Subscriptions() {
  const { user, profile } = useAuth();
  const { bookingEnabled, subscriptionEnabled } = useSettings();
  const { packages, vehicleCategories, individualRates } = useCatalog();
  const navigate = useNavigate();
  const [category, setCategory] = useState("hatchback_compact");
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(null);
  const [cancellingPlan, setCancellingPlan] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [hasCars, setHasCars] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [addForm, setAddForm] = useState({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
  const [addingSave, setAddingSave] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user) { setInitLoading(false); return; }
      try {
        const [subSnap, carsSnap] = await Promise.all([
          getDocs(query(
            collection(db, "subscriptions"),
            where("uid", "==", user.uid),
            where("status", "in", ["active", "pending_approval"])
          )),
          getDocs(query(collection(db, "cars"), where("uid", "==", user.uid))),
        ]);
        if (!subSnap.empty) {
          const d = subSnap.docs[0];
          setCurrentSub({ id: d.id, ...d.data() });
        }
        setHasCars(!carsSnap.empty);
      } finally {
        setInitLoading(false);
      }
    };
    load();
  }, [user]);

  const cat = vehicleCategories.find((c) => c.id === category) || vehicleCategories[0] || null;
  const priceKey = cat?.priceKey;

  const doSubscribe = async (plan) => {
    setLoading(plan.id);
    try {
      const priorQ = query(
        collection(db, "subscriptions"),
        where("uid", "==", user.uid),
        where("status", "in", ["active", "pending_approval"])
      );
      const priorSnap = await getDocs(priorQ);
      await Promise.all(
        priorSnap.docs.map((d) => updateDoc(doc(db, "subscriptions", d.id), { status: "cancelled" }))
      );
      const startDate = new Date();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + plan.durationMonths);
      const subRef = await addDoc(collection(db, "subscriptions"), {
        uid: user.uid,
        userPhone: user.phoneNumber || "",
        userName: profile?.name || user.phoneNumber || "Guest",
        packageId: plan.id,
        packageCode: plan.code,
        planName: plan.name,
        vehicleCategory: cat?.id || "",
        vehicleCategoryLabel: cat?.label || "",
        price: (priceKey && plan[priceKey]) ?? 0,
        washes: plan.washes,
        washesPerMonth: plan.washCount,
        washesRemaining: plan.washCount,
        durationMonths: plan.durationMonths,
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
        status: "pending_approval",
        paymentMode: "cash",
        createdAt: serverTimestamp(),
      });
      setCurrentSub({ id: subRef.id, planName: plan.name, packageId: plan.id, status: "pending_approval" });
      toast.success("Subscription request sent!");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setLoading(null);
    }
  };

  const handleSubscribe = (plan) => {
    if (!user) { navigate("/login"); return; }
    if (!hasCars) {
      setPendingPlan(plan);
      setShowAddVehicle(true);
      return;
    }
    doSubscribe(plan);
  };

  const handleAddVehicle = async () => {
    if (!addForm.name.trim()) { toast.error("Vehicle name is required"); return; }
    setAddingSave(true);
    try {
      await addDoc(collection(db, "cars"), {
        uid: user.uid,
        name: addForm.name.trim(),
        registration: addForm.registration.trim().toUpperCase(),
        category: addForm.category,
        createdAt: serverTimestamp(),
      });
      toast.success("Vehicle added");
      setHasCars(true);
      setShowAddVehicle(false);
      setAddForm({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
      if (pendingPlan) {
        const plan = pendingPlan;
        setPendingPlan(null);
        doSubscribe(plan);
      }
    } catch (err) { toast.error(err.message); }
    finally { setAddingSave(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        {initLoading ? <PageLoader label="Loading plans…" /> : (
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ PACKAGES ]</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mb-4">
            Pick a plan. Change. Cancel any time.
          </h1>
          <p className="text-lg text-[#525252] max-w-2xl mb-10">
            Multi-month bundles. Cash on first service. Cancel anytime.
          </p>

          {/* CATEGORY TOGGLE — this picks a PACKAGE PRICE TIER (priceHatchback vs
              priceSUV), not a literal vehicle type, so categories that share a
              priceKey (e.g. Sedan uses priceHatchback, same as Hatchback) are
              deduped to one button — showing both would just be two buttons
              with identical prices. Bike has no package pricing at all. */}
          <div className="inline-flex border border-[#0A0A0A] mb-12">
            {vehicleCategories
              .filter((c) => c.id !== "bike")
              .filter((c, i, arr) => arr.findIndex((x) => x.priceKey === c.priceKey) === i)
              .map((c) => (
              <button
                key={c.id}
                data-testid={`category-${c.id}`}
                onClick={() => setCategory(c.id)}
                className={`px-6 py-3 font-mono-accent text-xs ${category === c.id ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F4F4F5]"}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Pending request — friendly success message */}
          {currentSub?.status === "pending_approval" && (
            <div className="mb-10 border-2 border-[#00D05A] bg-[#E8FAF0] p-8">
              <div className="font-mono-accent text-xs text-[#00D05A] mb-3">● REQUEST SENT</div>
              <h2 className="font-display font-black text-3xl tracking-tighter mb-2">Request sent!</h2>
              <p className="text-[#525252] mb-1">We've notified the store.</p>
              <p className="text-[#525252]">Hang tight — our team will contact you shortly to get you set up.</p>
              <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                <div className="font-display font-bold text-lg">{currentSub.planName}</div>
                <CancelPlanDialog
                  disabled={cancellingPlan}
                  onCancel={async () => {
                    setCancellingPlan(true);
                    try {
                      await updateDoc(doc(db, "subscriptions", currentSub.id), { status: "cancelled" });
                      setCurrentSub(null);
                      toast.success("Request cancelled");
                    } catch (err) {
                      toast.error("Failed: " + err.message);
                    } finally {
                      setCancellingPlan(false);
                    }
                  }}
                  cancellingPlan={cancellingPlan}
                  label="Withdraw request"
                />
              </div>
            </div>
          )}

          {/* Active plan banner */}
          {currentSub?.status === "active" && (
            <div className="mb-6 border border-[#002FA7] bg-[#F4F4F5] p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono-accent text-xs text-[#002FA7]">CURRENT PLAN</div>
                <div className="font-display font-bold text-xl">{currentSub.planName}</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="font-mono-accent text-xs text-[#00D05A]">● ACTIVE</div>
                <CancelPlanDialog
                  disabled={cancellingPlan}
                  onCancel={async () => {
                    setCancellingPlan(true);
                    try {
                      await updateDoc(doc(db, "subscriptions", currentSub.id), { status: "cancelled" });
                      setCurrentSub(null);
                      toast.success("Plan cancelled");
                    } catch (err) {
                      toast.error("Failed: " + err.message);
                    } finally {
                      setCancellingPlan(false);
                    }
                  }}
                  cancellingPlan={cancellingPlan}
                  label="Cancel"
                />
              </div>
            </div>
          )}

          {currentSub?.status === "active" && (
            <div className="mb-12 text-sm text-[#525252]">
              Cancel your current plan to switch packages.
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D4D4D8] border border-[#D4D4D8]">
            {packages.map((plan) => {
              // Only lock buttons once store has approved (status === "active")
              const isCurrent = currentSub?.packageId === plan.id && currentSub?.status === "active";
              const blocked = !!currentSub && currentSub.status === "active" && !isCurrent;
              return (
                <div
                  key={plan.id}
                  data-testid={`plan-${plan.id}`}
                  className={`p-8 bg-white border-4 ${PLAN_BORDER_COLORS[plan.id] || "border-[#D4D4D8]"} ${blocked ? "opacity-60" : ""}`}
                >
                  <div className="font-mono-accent font-bold text-sm mb-3 text-[#002FA7]">
                    {plan.code}
                  </div>
                  <h3 className="font-display font-black text-2xl mb-1">{plan.name}</h3>
                  <div className="font-mono-accent text-[10px] mb-6 text-[#525252]">
                    {plan.durationMonths} MONTHS
                  </div>

                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="font-display font-black text-4xl">₹{(typeof plan[priceKey] === "number" ? plan[priceKey] : 0).toLocaleString("en-IN")}</span>
                  </div>

                  <div className="text-sm mb-8 text-[#525252]">
                    {plan.washes}
                  </div>

                  <ul className="space-y-2 mb-8 text-xs">
                    <Feat>{plan.washCount} total washes</Feat>
                    <Feat>{plan.durationMonths}-month validity</Feat>
                    <Feat>For {cat?.short || "—"}</Feat>
                    <Feat>Cash on service</Feat>
                  </ul>

                  <Button
                    data-testid={`subscribe-${plan.id}-button`}
                    onClick={() => handleSubscribe(plan)}
                    disabled={loading === plan.id || isCurrent || blocked || !subscriptionEnabled}
                    className={`w-full rounded-none font-bold uppercase tracking-wider py-5 text-xs h-auto ${
                      isCurrent ? "bg-[#00D05A] text-black"
                      : blocked || !subscriptionEnabled ? "bg-[#D4D4D8] text-[#525252] cursor-not-allowed"
                      : "bg-[#002FA7] text-white hover:bg-[#001B6B]"
                    }`}
                  >
                    {isCurrent ? "Current plan"
                      : blocked ? "Plan already active"
                      : !subscriptionEnabled ? "Coming Soon"
                      : loading === plan.id ? <Spinner /> : "Request Subscription"}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* INDIVIDUAL RATES */}
          <div className="mt-24">
            <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ INDIVIDUAL WASH RATES ]</div>
            <h2 className="font-display font-black text-3xl tracking-tighter mb-8">
              Just one wash? No problem.
            </h2>
            <div className="border border-[#D4D4D8] overflow-x-auto">
              <div className="grid grid-cols-3 gap-px bg-[#D4D4D8] min-w-[500px]">
                <div className="bg-[#0A0A0A] text-white p-4 font-mono-accent text-[10px]">WASH CATEGORY</div>
                <div className="bg-[#0A0A0A] text-white p-4 font-mono-accent text-[10px] text-right">INTERIOR + EXTERIOR</div>
                <div className="bg-[#0A0A0A] text-white p-4 font-mono-accent text-[10px] text-right">EXTERIOR ONLY</div>
                {individualRates
                  .filter((r) => typeof r.interiorExterior === "number")
                  .map((r) => (
                    <RowGroup key={r.id} label={r.label} ie={r.interiorExterior} eo={r.exteriorOnly} />
                  ))}
              </div>
            </div>

            {/* Bike has its own flat-rate model (no interior/exterior split), so it gets its own row. */}
            {individualRates.filter((r) => typeof r.washPolish === "number").map((r) => (
              <div key={r.id} className="mt-4 border border-[#D4D4D8] p-6 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="font-mono-accent text-[10px] text-[#002FA7] mb-1">BIKE</div>
                  <div className="font-display font-bold text-lg">{r.label}</div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-right">
                    <div className="font-mono-accent text-[10px] text-[#525252]">WASH & POLISH</div>
                    <div className="font-display font-bold text-xl">₹{r.washPolish}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-accent text-[10px] text-[#525252]">+ WAXING</div>
                    <div className="font-display font-bold text-xl">+₹{r.waxing}</div>
                  </div>
                </div>
              </div>
            ))}

            {bookingEnabled ? (
              <Link to="/book" className="inline-block mt-6 font-mono-accent text-xs text-[#002FA7] hover:underline">
                BOOK A SINGLE WASH →
              </Link>
            ) : (
              <span className="inline-block mt-6 font-mono-accent text-xs text-[#D4D4D8]">
                BOOK A SINGLE WASH — COMING SOON
              </span>
            )}
          </div>
        </div>
        )}
      </main>

      {/* Add Vehicle dialog — shown when user has no car and tries to subscribe */}
      <Dialog open={showAddVehicle} onOpenChange={(open) => { setShowAddVehicle(open); if (!open) setPendingPlan(null); }}>
        <DialogContent className="max-w-md rounded-none border-[#D4D4D8]">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tighter">Add your vehicle first</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#525252] -mt-2 mb-2">We need your vehicle details before setting up a subscription.</p>
          <div className="space-y-4">
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">VEHICLE NAME</label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g. My Swift, Dad's SUV…"
                className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
              />
            </div>
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">CATEGORY</label>
              <Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}>
                <SelectTrigger className="rounded-none border-[#D4D4D8]">
                  <SelectValue placeholder="Select type…" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {vehicleCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">REGISTRATION (OPTIONAL)</label>
              <Input
                value={addForm.registration}
                onChange={(e) => setAddForm({ ...addForm, registration: e.target.value.toUpperCase() })}
                placeholder="KA-01-AB-1234"
                style={{ textTransform: "uppercase" }}
                className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12 font-mono"
              />
            </div>
            <Button
              onClick={handleAddVehicle}
              disabled={addingSave}
              className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12"
            >
              {addingSave ? <Spinner /> : "Save Vehicle & Continue"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

const CancelPlanDialog = ({ onCancel, cancellingPlan, disabled, label = "Cancel" }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button
        data-testid="cancel-subscription-button"
        type="button"
        disabled={disabled}
        className="font-mono-accent text-[10px] uppercase border-2 border-[#FF2A2A] text-[#FF2A2A] hover:bg-[#FF2A2A] hover:text-white px-3 py-2 disabled:opacity-50 flex items-center gap-1"
      >
        {cancellingPlan ? <Spinner /> : label}
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="rounded-none border-[#D4D4D8]">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display font-black text-2xl tracking-tighter">Cancel plan?</AlertDialogTitle>
        <AlertDialogDescription className="text-[#525252]">
          This will cancel your current plan. You'll be free to subscribe to a different package.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-none font-bold uppercase tracking-wide text-xs">Keep plan</AlertDialogCancel>
        <AlertDialogAction
          className="rounded-none bg-[#FF2A2A] hover:bg-[#CC0000] font-bold uppercase tracking-wide text-xs"
          onClick={onCancel}
        >
          Yes, cancel
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const Feat = ({ children }) => (
  <li className="flex items-start gap-2">
    <Check size={12} strokeWidth={2.5} className="text-[#002FA7] mt-1" />
    <span>{children}</span>
  </li>
);

const RowGroup = ({ label, ie, eo }) => (
  <>
    <div className="bg-white p-4 font-bold text-sm">{label}</div>
    <div className="bg-white p-4 text-right font-display font-bold text-lg">₹{ie}</div>
    <div className="bg-white p-4 text-right font-display font-bold text-lg">₹{eo}</div>
  </>
);
